import { useMutation } from '@tanstack/react-query';

import { axiosInt, API_CORE_URL } from '../../..';
import { useMessage } from '../../../hooks/useMessage';
import type { SupportedLanguage } from '../../../translate';
import type { EpiDocumentType, EpiInspectResult } from '../types/types';

export const INSPECT_EPI_IMPORT_KEY = 'inspectEpiImport';

interface IInspectRequest {
  file: File;
  /** Which ePI model version — the catalogue is read from its schema. */
  modelVersionId: string;
  /** The language the document is written in. */
  language: SupportedLanguage;
  /**
   * Reads the file as this one composition, in pre-QRD mode. Left out, the
   * file is read as a whole ePI.
   */
  composition?: EpiDocumentType;
}

/**
 * Failures that say nothing about the document. Left to the generic message
 * they read as "your file is wrong", which sends the author off inspecting a
 * file that was fine.
 */
const messageForStatus = (status?: number): string | undefined => {
  if (status === 401 || status === 403) {
    return 'You do not have permission to import documents.';
  }
  if (status === 413) {
    return 'That document is too large to upload.';
  }
  if (status && status >= 500) {
    return 'The server could not read the document. Please try again.';
  }
  return undefined;
};

const inspectApi = async ({
  file,
  modelVersionId,
  language,
  composition,
}: IInspectRequest): Promise<EpiInspectResult> => {
  const body = new FormData();
  body.append('file', file);
  body.append('modelVersionId', modelVersionId);
  body.append('language', language);
  if (composition) body.append('composition', composition);

  const response = await axiosInt.post<EpiInspectResult>(
    `${API_CORE_URL}/epi/import/docx/inspect`,
    body
  );
  return response.data;
};

/**
 * Reads an authored DOCX and returns the ePI it would become, saving nothing.
 *
 * Nothing is sent but the document, the model version, its language and
 * optionally the one composition it holds: the name, the procedure number,
 * the family and the products belong to the ePI being edited, and `inspect` neither reads nor returns them. Applying the
 * result is the caller's job — see `applyImport` in the ePI context.
 */
export const useInspectEpiImport = () => {
  const message = useMessage();

  const {
    mutate: inspectImport,
    isPending: isInspecting,
    data: inspected,
    reset: resetInspection,
  } = useMutation<EpiInspectResult, unknown, IInspectRequest>({
    mutationKey: [INSPECT_EPI_IMPORT_KEY],
    mutationFn: inspectApi,
    onError: error => {
      const response = (
        error as {
          response?: {
            status?: number;
            data?: { message?: string; detail?: string };
          };
        }
      )?.response;
      // The status wins where it is one of these: a server's own wording for
      // a 403 is "Access Denied", which is no help to somebody importing.
      message.error(
        messageForStatus(response?.status) ??
          response?.data?.message ??
          response?.data?.detail ??
          'Could not read the document. Please check it is an authored ePI.'
      );
    },
  });

  return { inspectImport, isInspecting, inspected, resetInspection };
};
